/*
 * User Information Service - provides processed information about the user, particularly their permissions.
 * Angular module: trackerbase-app.services.user-info-service
 * Exposed Service: UserInfoService
 *
 */
import angular from "angular";

import Assert from "tbAssert";
import AuthServiceModule from "tbClient/services/auth";
import AuthServiceModuleConstants from "tbClient/services/auth/AuthConstants";
import UserInfoServiceConstants from "./UserInfoServiceConstants";
import TbAppConfigDataServiceModule from "../config/tb-app-config/TbAppConfigDataService";

//const abAdfsUserCheckRegExp = /^adfs\|AB21ADFS\|[A-Za-z\d_\-.]+@americanbridge\.local$/;

const mAuthSvc = new WeakMap();
const mLogSvc = new WeakMap();

const mAuthProfile = new WeakMap();
const mIsTBUser = new WeakMap();
const mIsInternalUser = new WeakMap();
const mIsTracker = new WeakMap();
const mIsTrackingManager = new WeakMap();
const mIsITAdministrator = new WeakMap();
const mUserGroups = new WeakMap();

const mUserCheckRegExp = new WeakMap();


class UserInfoService {
    constructor($log, TbAuthService, TbAppConfigDataService) {
        Assert.StrictAssert($log != null, "Assertion Failure: User Info Service Missing Log Service Dependency");
        Assert.StrictAssert(TbAuthService != null, "Assertion Failure: User Info Service Missing Auth Service Dependency");
        Assert.StrictAssert(TbAppConfigDataService != null, "Assertion Failure: Missing TrackerBase Application Configuration Data Service Dependency");

        mLogSvc.set(this, $log);
        mAuthSvc.set(this, TbAuthService);

        mAuthProfile.set(this, null);
        mIsTBUser.set(this, false);
        mIsInternalUser.set(this, false);
        mIsTracker.set(this, false);
        mIsTrackingManager.set(this, false);
        mIsITAdministrator.set(this, false);
        mUserGroups.set(this, []);

        mUserCheckRegExp.set(this, this._buildUserCheckPattern(TbAppConfigDataService));
    }

    _buildUserCheckPattern(appConfigDataSvc) {
        const logSvc = mLogSvc.get(this);

        let userCheckPattern;

        if (appConfigDataSvc.tbAuthConnectionType === "oidc" && appConfigDataSvc.tbAuthConnectionName === "onelogin") {
            // OneLogin user IDs are simple numeric strings
            userCheckPattern = String.raw`^\d+$`;
            logSvc.debug("Built OneLogin User Check Pattern:", userCheckPattern);
        } else {
            // Legacy Auth0 ADFS pattern
            let userCheckPatternParts = [
                String.raw`^${appConfigDataSvc.tbAuthConnectionType}\|`
            ];

            if (appConfigDataSvc.tbAuthConnectionName != null) {
                userCheckPatternParts.push(String.raw`${appConfigDataSvc.tbAuthConnectionName}\|`);
            }

            userCheckPatternParts.push(String.raw`[A-Za-z\d_\-.]+@americanbridge\.local$`);
            userCheckPattern = userCheckPatternParts.join("");
            logSvc.debug("Built Auth0 ADFS User Check Pattern:", userCheckPattern);
        }

        return new RegExp(userCheckPattern);
    }

    _searchGroupList(groupToFind) {
        let groupSearchIndex = mUserGroups.get(this).findIndex((elem) => (elem === groupToFind));
        return (groupSearchIndex >= 0);
    }

    _onAuthorizationFail() {
        mIsTBUser.set(this, false);
        mIsInternalUser.set(this, false);
        mIsTracker.set(this, false);
        mIsTrackingManager.set(this, false);
        mIsITAdministrator.set(this, false);
        mAuthProfile.set(this, null);
        mUserGroups.get(this).length = 0;
    }

    toString() {
        let authProfileName = this.userName;
        if (authProfileName == null) {
            authProfileName = "(Not Authenticated)";
        }
        return `User Info Service: ${authProfileName}`;
    }

    reload() {
        let authSvc = mAuthSvc.get(this);

        return Promise.resolve()
            .then(() => {
                if (authSvc.isAuthenticated() === true) {
                    return this._reloadIfAuthenticated();
                }
                else {
                    throw new Error("Cannot reload User Info Service: Not Authenticated");
                }
            });
    }

    _reloadIfAuthenticated() {
        const authSvc = mAuthSvc.get(this);
        const userCheckRegExp = mUserCheckRegExp.get(this);

        const readGroupsFromProfile = (profile) => {
            // Try standard groups claim first, then fall back to custom claim
            const standardGroups = profile.groups;
            const customGroups = profile[`${AuthServiceModuleConstants.claims.namespace}groups`];
            
            mLogSvc.get(this).debug("OneLogin Profile Groups Debug:", {
                standardGroups: standardGroups,
                customGroups: customGroups,
                profileKeys: Object.keys(profile)
            });
            
            return standardGroups || customGroups;
        };

        const readUserIdFromProfile = (profile) => profile.sub;

        return authSvc.getProfile()
            .then((profile) => {
                let authorizationCompleted = false;
                mAuthProfile.set(this, profile);
                let authProfile = mAuthProfile.get(this);

                let isValidUser = userCheckRegExp.test(readUserIdFromProfile(authProfile));

                if (isValidUser) {
                    mUserGroups.get(this).length = 0;
                    let profileGroups = readGroupsFromProfile(authProfile);
                    
                    mLogSvc.get(this).debug("Processing groups for user authorization:", {
                        userId: readUserIdFromProfile(authProfile),
                        rawGroups: profileGroups,
                        groupsType: angular.isArray(profileGroups) ? "array" : angular.isString(profileGroups) ? "string" : typeof profileGroups
                    });
                    
                    if (angular.isArray(profileGroups)) {
                        Array.prototype.push.apply(mUserGroups.get(this), profileGroups);
                    }
                    else if (angular.isString(profileGroups)) {
                        mUserGroups.get(this).push(profileGroups);
                    }
                    
                    mLogSvc.get(this).debug("Final processed groups for authorization:", mUserGroups.get(this));

                    const userGroups = UserInfoServiceConstants.groups;
                    let isExternalTBUser = this._searchGroupList(userGroups.externalTbUsers);

                    let isInternalTBUser = this._searchGroupList(userGroups.internalTbUsers);

                    if (isExternalTBUser || isInternalTBUser) {
                        mIsTBUser.set(this, true);
                        mIsInternalUser.set(this, isInternalTBUser);
                    }

                    if ((mIsTBUser.get(this) === true) && (mIsInternalUser.get(this) === true)) {
                        mIsTracker.set(this, this._searchGroupList(userGroups.trackers));
                        mIsTrackingManager.set(this, this._searchGroupList(userGroups.trackingManagers));
                        mIsITAdministrator.set(this, this._searchGroupList(userGroups.admins));
                    }

                    authorizationCompleted = true;
                }

                if (!authorizationCompleted) {
                    mLogSvc.get(this).debug("UserInfoService: Authorization did not finish. Clearing authorizations.");
                    this._onAuthorizationFail();
                }
            })
            .catch((err) => {
                mLogSvc.get(this).warning(
                    "UserInfoService: Clearing user authorizations because processing failed.", err);
                this._onAuthorizationFail();
            })
            .then(() => {
                mLogSvc.get(this).debug("UserInfoService: Reloaded.");
            });
    }

    get userName() {
        let authProfile = mAuthProfile.get(this);
        if (authProfile != null) {
            let userNameFromProfile = angular.copy(authProfile.name);
            return userNameFromProfile;
        }
        else {
            return undefined;
        }
    }

    get isTrackerBaseUser() {
        return mIsTBUser.get(this);
    }

    get isInternalUser() {
        return mIsInternalUser.get(this);
    }

    get isTracker() {
        return mIsTracker.get(this);
    }

    get isTrackingManager() {
        return mIsTrackingManager.get(this);
    }

    get isITAdministrator() {
        return mIsITAdministrator.get(this);
    }

    get hasAdminRights() {
        let adminRights = this.isITAdministrator;
        return adminRights;
    }

    get hasManagementRights() {
        let managerRights = (this.isTrackingManager || this.hasAdminRights);
        return managerRights;
    }

    get hasTrackerRights() {
        let trackerRights = (this.isTracker || this.hasManagementRights);
        return trackerRights;
    }

    get hasInternalUserRights() {
        let internalUserRights = (this.isInternalUser || this.hasTrackerRights);
        return internalUserRights;
    }

    get hasExternalUserRights() {
        let externalUserRights = (this.isTrackerBaseUser || this.hasInternalUserRights);
        return externalUserRights;
    }

    logUserRights() {
        mLogSvc.get(this).debug("Is Tracker Base User: " + (this.isTrackerBaseUser ? "Yes" : "No"));
        mLogSvc.get(this).debug("Is Internal User: " + (this.isInternalUser ? "Yes" : "No"));
        mLogSvc.get(this).debug("Is Tracker: " + (this.isTracker ? "Yes" : "No"));
        mLogSvc.get(this).debug("Is Tracking Manager: " + (this.isTrackingManager ? "Yes" : "No"));
        mLogSvc.get(this).debug("Is IT Administrator: " + (this.isITAdministrator ? "Yes" : "No"));
        mLogSvc.get(this).debug("Has External User Rights: " + (this.hasExternalUserRights ? "Yes" : "No"));
        mLogSvc.get(this).debug("Has Internal User Rights: " + (this.hasInternalUserRights ? "Yes" : "No"));
        mLogSvc.get(this).debug("Has Tracker Rights: " + (this.hasTrackerRights ? "Yes" : "No"));
        mLogSvc.get(this).debug("Has Management Rights: " + (this.hasManagementRights ? "Yes" : "No"));
        mLogSvc.get(this).debug("Has Administrator Rights: " + (this.hasAdminRights ? "Yes" : "No"));
    }
}

UserInfoService.$inject = [ "$log", "TbAuthService", "TbAppConfigDataService" ];

export default angular
    .module("trackerbase-app.services.user-info-service", [
        AuthServiceModule,
        TbAppConfigDataServiceModule
    ])
    .service("UserInfoService", UserInfoService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/UserInfoService.js
// module id = 4+pU
// module chunks = 1